import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Typography,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { supabase } from '../supabaseClient';

const LocalChatwootSetupDialog = ({ open, onClose, initialIntegrationId = null }) => {
  const initialCw = process.env.REACT_APP_CHATWOOT_HOST || (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_CHATWOOT_HOST) || '';
  const [chatwootHost, setChatwootHost] = useState(initialCw);
  const [targetEmail, setTargetEmail] = useState('');

  const [createAccount, setCreateAccount] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [existingAccountId, setExistingAccountId] = useState('');
  const [makeUserAdmin, setMakeUserAdmin] = useState(true);

  const [createAgentBot, setCreateAgentBot] = useState(true);
  const [botName, setBotName] = useState('RAG Agent Bot');
  const [botDescription, setBotDescription] = useState('RAG Agent Bot');
  const [attachBotToInbox, setAttachBotToInbox] = useState(true);

  const [createInbox, setCreateInbox] = useState(true);
  const [inboxName, setInboxName] = useState('RAG Web Chat');
  const [websiteUrl, setWebsiteUrl] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [embedSnippet, setEmbedSnippet] = useState('');
  const [websiteToken, setWebsiteToken] = useState('');
  const [resolvedAccountId, setResolvedAccountId] = useState('');
  const [enableWebSearch, setEnableWebSearch] = useState(false);
  const [savingWeb, setSavingWeb] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);
  
  // E-commerce integration state
  const [enableEcommerceLookup, setEnableEcommerceLookup] = useState(false);
  const [connectedStores, setConnectedStores] = useState([]);
  const [selectedEcommerceStoreId, setSelectedEcommerceStoreId] = useState('');

  const [userPassword, setUserPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // NEW: Integration management state
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(initialIntegrationId);
  const [isCreatingNew, setIsCreatingNew] = useState(!initialIntegrationId);
  const [integrationName, setIntegrationName] = useState('');
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const generateStrongPassword = () => {
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const nums = '0123456789';
    const special = '!@#$%^&*()_+-=[]{}|"/\\.,`<>:;?~';
    const pick = (s) => s[Math.floor(Math.random() * s.length)];
    let pwd = pick(upper) + pick(lower) + pick(nums) + pick(special);
    const pool = upper + lower + nums + special;
    for (let i = 0; i < 12; i++) pwd += pick(pool);
    setUserPassword(pwd);
  };

  // NEW: Load existing integrations list
  useEffect(() => {
    const loadIntegrations = async () => {
      if (!open) return;
      
      setLoadingIntegrations(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (!userId) return;
        
        const { data, error } = await supabase
          .rpc('get_user_chatwoot_integrations_summary', { p_user_id: userId });
        
        if (error) {
          console.error('Failed to load integrations:', error);
          return;
        }
        
        setIntegrations(data || []);
        
        // If initialIntegrationId was passed, select it
        if (initialIntegrationId) {
          setSelectedIntegrationId(initialIntegrationId);
          setIsCreatingNew(false);
        }
      } catch (e) {
        console.error('Failed to load integrations:', e);
        setError(`Failed to load integrations: ${e.message}`);
      } finally {
        setLoadingIntegrations(false);
      }
    };
    
    loadIntegrations();
  }, [open, initialIntegrationId]);

  // Load connected e-commerce stores for the dropdown
  useEffect(() => {
    const loadConnectedStores = async () => {
      if (!open) return;
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const resp = await fetch(`${BACKEND_URL}/market-api/integrations/connected`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (resp.ok) {
          const data = await resp.json();
          setConnectedStores(data || []);
        }
      } catch (e) {
        console.error('Failed to load connected stores:', e);
      }
    };
    
    loadConnectedStores();
  }, [open]);

  // Auto-fill user email from Supabase session for new integrations
  useEffect(() => {
    const loadUserEmail = async () => {
      if (!open || !isCreatingNew) return;
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userEmail = session?.user?.email;
        if (userEmail && !targetEmail) {
          setTargetEmail(userEmail);
        }
      } catch (e) {
        console.error('Failed to load user email:', e);
      }
    };
    
    loadUserEmail();
  }, [open, isCreatingNew, targetEmail]);

  // Auto-sync bot and inbox names with integration name
  useEffect(() => {
    if (isCreatingNew && integrationName.trim()) {
      const autoName = `${integrationName} Agent`;
      const autoDescription = `AI Agent for ${integrationName}`;
      
      // Only auto-set if user hasn't manually changed them
      if (botName === 'RAG Agent Bot' || botName === '') {
        setBotName(autoName);
      }
      if (botDescription === 'RAG Agent Bot' || botDescription === '') {
        setBotDescription(autoDescription);
      }
      if (inboxName === 'RAG Web Chat' || inboxName === '') {
        setInboxName(integrationName);
      }
    }
  }, [integrationName, isCreatingNew, botName, botDescription, inboxName]);

  // Prefill from Supabase configuration when integration is selected
  useEffect(() => {
    const loadConfig = async () => {
      if (!selectedIntegrationId || isCreatingNew) return;
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (!userId) return;
        
        const { data, error } = await supabase
          .from('chatwoot_configurations')
          .select('*')
          .eq('id', selectedIntegrationId)
          .eq('rag_user_id', userId)
          .maybeSingle();
        
        if (error) {
          console.error('Failed to load integration data:', error);
          return;
        }
        
        if (data) {
          // Load integration name
          if (data.integration_name) setIntegrationName(data.integration_name);
          
          // Load flags
          if (typeof data.enable_web_search === 'boolean') setEnableWebSearch(data.enable_web_search);
          if (typeof data.requires_approval === 'boolean') setRequiresApproval(data.requires_approval);
          
          // Load provision settings
          const ps = data.provision_settings || {};
          if (ps.chatwoot_host) setChatwootHost(ps.chatwoot_host);
          if (ps.target_user_email) setTargetEmail(ps.target_user_email);
          if (typeof ps.create_account === 'boolean') setCreateAccount(ps.create_account);
          if (ps.account_name) setAccountName(ps.account_name);
          if (ps.existing_account_id) setExistingAccountId(String(ps.existing_account_id));
          if (typeof ps.make_user_admin === 'boolean') setMakeUserAdmin(ps.make_user_admin);
          if (typeof ps.create_agent_bot === 'boolean') setCreateAgentBot(ps.create_agent_bot);
          if (ps.bot_name) setBotName(ps.bot_name);
          if (ps.description) setBotDescription(ps.description);
          if (typeof ps.attach_bot_to_inbox === 'boolean') setAttachBotToInbox(ps.attach_bot_to_inbox);
          if (typeof ps.create_new_inbox === 'boolean') setCreateInbox(ps.create_new_inbox);
          if (ps.inbox_name) setInboxName(ps.inbox_name);
          if (ps.website_url) setWebsiteUrl(ps.website_url);
          
          // Load embed script if exists
          if (data.embed_script) setEmbedSnippet(data.embed_script);
        }
      } catch (e) {
        console.error('Failed to load integration config:', e);
      }
    };
    
    if (open) loadConfig();
  }, [open, selectedIntegrationId, isCreatingNew]);

  const handleProvision = async () => {
    // Validation
    if (isCreatingNew && !integrationName.trim()) {
      setError('Integration name is required when creating a new integration.');
      return;
    }
    
    // Confirmation prompt to avoid accidental provisioning
    try {
      const summary = [
        isCreatingNew ? `New Integration: ${integrationName}` : `Update Integration: ${integrationName}`,
        `Target Email: ${targetEmail || '-'}`,
        `Create Account: ${createAccount ? 'Yes' : 'No'}`,
        createAccount ? `Account Name: ${accountName || '-'}` : null,
        !createAccount && existingAccountId ? `Existing Account ID: ${existingAccountId}` : null,
        `Make User Admin: ${makeUserAdmin ? 'Yes' : 'No'}`,
        `Create Agent Bot: ${createAgentBot ? 'Yes' : 'No'}`,
        createAgentBot ? `Bot Name: ${botName || '-'}` : null,
        createAgentBot ? `Attach Bot to Inbox: ${attachBotToInbox ? 'Yes' : 'No'}` : null,
        `Create Inbox: ${createInbox ? 'Yes' : 'No'}`,
        createInbox ? `Inbox Name: ${inboxName || '-'}` : null,
        createInbox ? `Website URL: ${websiteUrl || '-'}` : null,
        `Enable Web Search: ${enableWebSearch ? 'Yes' : 'No'}`
      ].filter(Boolean).join('\n');
      const ok = window.confirm(`Proceed with ${isCreatingNew ? 'creating' : 'updating'} this integration?\n\n${summary}`);
      if (!ok) return;
    } catch (_) {}

    setSaving(true);
    setError('');
    setSuccess('');
    setEmbedSnippet('');
    setWebsiteToken('');

    try {
      if (!targetEmail.trim()) {
        setError('Target user email is required.');
        setSaving(false);
        return;
      }
      if (createAccount && !accountName.trim()) {
        setError('Account name is required when creating an account.');
        setSaving(false);
        return;
      }

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw new Error(`Supabase auth error: ${sessionError.message}`);
      if (!session?.access_token) throw new Error('No active user session found. Please log in.');

      const payload = {
        chatwoot_host: chatwootHost?.trim() || undefined,
        target_user_email: targetEmail.trim(),
        existing_account_id: existingAccountId ? parseInt(existingAccountId, 10) : undefined,
        create_account: !!createAccount,
        account_name: createAccount ? accountName.trim() : undefined,
        make_user_admin: !!makeUserAdmin,
        create_agent_bot: !!createAgentBot,
        bot_name: createAgentBot ? (botName?.trim() || 'RAG Agent Bot') : undefined,
        description: createAgentBot ? (botDescription?.trim() || 'RAG Agent Bot') : undefined,
        attach_bot_to_inbox: !!attachBotToInbox,
        create_new_inbox: !!createInbox,
        inbox_name: createInbox ? (inboxName?.trim() || 'RAG Web Chat') : undefined,
        website_url: createInbox ? (websiteUrl?.trim() || undefined) : undefined,
        user_password: userPassword?.trim() || undefined,
        enable_web_search: enableWebSearch === true,
        integration_name: integrationName?.trim() || undefined,  // NEW
        integration_id: isCreatingNew ? undefined : selectedIntegrationId,  // NEW
      };

      const resp = await fetch('/chatwoot/local/provision', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
        const detailStr = typeof errData === 'string' ? errData : (errData.detail || errData.message || resp.statusText);
        // downgrade password policy errors to a friendly warning
        if (detailStr && /password/i.test(detailStr)) {
          setError('Password does not meet policy. Please include uppercase, lowercase, number, special, min 6 chars.');
        } else {
          setError(detailStr);
        }
        setSaving(false);
        return;
      }

      const data = await resp.json();
      setResolvedAccountId(data.resolved_account_id ? String(data.resolved_account_id) : '');
      if (data.website_token) setWebsiteToken(data.website_token);
      if (data.embed_script) setEmbedSnippet(data.embed_script);

      // Optionally reflect saved flag (best-effort)
      try {
        setEnableWebSearch(Boolean(data.enable_web_search));
      } catch (_) {}

      setSuccess('Local Chatwoot provisioning completed successfully.');
    } catch (e) {
      setError(e.message || 'Provisioning failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet || '');
      setSuccess('Embed snippet copied to clipboard.');
    } catch (_) {
      setError('Failed to copy embed snippet.');
    }
  };

  const handleSaveEmbed = async () => {
    try {
      setError('');
      const snippet = (embedSnippet || '').trim();
      if (!snippet) {
        setError('Embed snippet is empty.');
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        setError('No active user session found. Please log in.');
        return;
      }
      const { error: upErr } = await supabase
        .from('chatwoot_configurations')
        .update({ embed_script: snippet, updated_at: new Date().toISOString() })
        .eq('rag_user_id', userId);
      if (upErr) {
        setError(`Failed to save embed snippet: ${upErr.message}`);
        return;
      }
      setSuccess('Embed snippet saved to configuration.');
    } catch (e) {
      setError(e.message || 'Failed to save embed snippet.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="local-chatwoot-setup-title" maxWidth="md" fullWidth>
      <DialogTitle id="local-chatwoot-setup-title">Local Chatwoot Settings</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Provision a Chatwoot account membership for the user email below and optionally create an Agent Bot and a Web Widget inbox.
          All actions run server-side via secure internal endpoints. Nothing is added to your staff's agent list.
        </DialogContentText>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* NEW: Integration Selector Section */}
        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
          <Typography variant="h6" gutterBottom>
            Integration Management
          </Typography>
          
          {loadingIntegrations ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Integration</InputLabel>
                <Select
                  value={isCreatingNew ? 'new' : selectedIntegrationId || 'new'}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'new') {
                      setIsCreatingNew(true);
                      setSelectedIntegrationId(null);
                      // Clear form
                      setIntegrationName('');
                      setTargetEmail('');
                      setAccountName('');
                      setExistingAccountId('');
                      setBotName('RAG Agent Bot');
                      setBotDescription('RAG Agent Bot');
                      setInboxName('RAG Web Chat');
                      setWebsiteUrl('');
                      setEmbedSnippet('');
                      setWebsiteToken('');
                    } else {
                      setIsCreatingNew(false);
                      setSelectedIntegrationId(val);
                    }
                  }}
                >
                  <MenuItem value="new">
                    <em>➕ Create New Integration</em>
                  </MenuItem>
                  {integrations.length > 0 && <Divider />}
                  {integrations.map(int => (
                    <MenuItem key={int.integration_id} value={int.integration_id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <span>{int.integration_name}</span>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip 
                            label={`${int.file_count} files`} 
                            size="small" 
                            variant="outlined"
                          />
                          {!int.is_active && (
                            <Chip label="Inactive" size="small" color="default" />
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {isCreatingNew ? (
                <TextField
                  required
                  label="Integration Name"
                  value={integrationName}
                  onChange={e => setIntegrationName(e.target.value)}
                  fullWidth
                  autoFocus
                  placeholder="e.g., Main Website, Support Portal, Product Documentation"
                  helperText="Give this integration a descriptive name. This will be used to name your bot and inbox."
                  sx={{ mb: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="This name identifies your integration. Examples: 'Company Website', 'Support Portal', 'Product Docs'. It will be used to auto-name your bot and inbox.">
                          <InfoIcon fontSize="small" color="primary" />
                        </Tooltip>
                      </InputAdornment>
                    )
                  }}
                />
              ) : (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Editing: <strong>{integrationName}</strong>
                  {integrations.find(i => i.integration_id === selectedIntegrationId) && (
                    <Box component="span" sx={{ ml: 1 }}>
                      ({integrations.find(i => i.integration_id === selectedIntegrationId).file_count} files mapped)
                    </Box>
                  )}
                </Alert>
              )}
            </>
          )}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, mb: 2 }}>
          <TextField
            label="Chatwoot Host"
            value={chatwootHost}
            onChange={e => setChatwootHost(e.target.value)}
            fullWidth
            disabled
            helperText="Your Chatwoot server URL (system configured)"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="This is your Chatwoot installation URL. It's configured at the system level and cannot be changed per integration.">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
          <TextField
            required
            label="Target User Email"
            type="email"
            value={targetEmail}
            onChange={e => setTargetEmail(e.target.value)}
            fullWidth
            disabled
            helperText="This integration will be linked to your account"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="The Chatwoot account will be created for this email address. This is your logged-in user email and cannot be changed.">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />

          <FormControlLabel
            control={<Checkbox checked={createAccount} onChange={e => setCreateAccount(e.target.checked)} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>Create new Chatwoot account</span>
                <Tooltip title="Enable this if this is your first integration. If you already have a Chatwoot account from a previous integration, leave this unchecked.">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
            }
          />
          {createAccount && (
            <TextField
              required
              label="Account Name"
              value={accountName}
              onChange={e => setAccountName(e.target.value)}
              fullWidth
            />
          )}

          {!createAccount && (
            <TextField
              label="Existing Account ID (optional)"
              type="number"
              value={existingAccountId}
              onChange={e => setExistingAccountId(e.target.value)}
              fullWidth
              helperText="If not provided, backend will try to resolve by email membership."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="If you have an existing Chatwoot account ID, enter it here. Otherwise, leave blank and the system will find your account automatically.">
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}

          {/* Password for new or existing user membership (optional) */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
            <TextField
              label="User Password (optional)"
              type={showPassword ? 'text' : 'password'}
              value={userPassword}
              onChange={e => setUserPassword(e.target.value)}
              fullWidth
              helperText="Leave blank for auto-generated password. Click lock icon to generate strong password."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="A password is required for Chatwoot login. Leave blank to auto-generate a secure password, or enter your own.">
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                    <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(s => !s)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                    <IconButton aria-label="generate strong password" onClick={generateStrongPassword} edge="end" title="Generate strong password">
                      <SecurityIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <FormControlLabel
            control={<Checkbox checked={makeUserAdmin} onChange={e => setMakeUserAdmin(e.target.checked)} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>Make user admin in the account</span>
                <Tooltip title="Recommended for first integration. Admins can manage settings, add team members, and configure inboxes.">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
            }
          />

        {/* Scoped settings block: Save below only updates these two flags */}
        <Box sx={{ mt: 1, mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <DialogContentText sx={{ fontWeight: 600 }}>
              AI Response Settings
            </DialogContentText>
            <Tooltip title="These settings control how the AI agent responds to customer queries. You can change these anytime.">
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>
          
          <FormControlLabel
            control={<Checkbox checked={enableWebSearch} onChange={e => setEnableWebSearch(e.target.checked)} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>Use web search for replies</span>
                <Tooltip title="When enabled, the AI can search the internet to supplement answers from your knowledge base. Useful for general questions or current information.">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
            }
          />
          <FormControlLabel
            control={<Checkbox checked={requiresApproval} onChange={e => setRequiresApproval(e.target.checked)} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>Require approval before sending answers</span>
                <Tooltip title="When enabled, AI-generated responses will be sent as private notes to your agents for review before being sent to customers. Recommended for critical or sensitive support.">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
            }
          />
          
          {/* E-commerce Order Lookup */}
          <FormControlLabel
            control={<Checkbox checked={enableEcommerceLookup} onChange={e => setEnableEcommerceLookup(e.target.checked)} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>Enable e-commerce order lookup</span>
                <Tooltip title="When enabled, customers can ask about their orders, delivery status, and tracking info. The AI will fetch real-time data from your connected e-commerce store.">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
            }
          />
          {enableEcommerceLookup && (
            <Box sx={{ ml: 3, mt: 1, mb: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Select E-commerce Store</InputLabel>
                <Select
                  value={selectedEcommerceStoreId}
                  onChange={e => setSelectedEcommerceStoreId(e.target.value)}
                  label="Select E-commerce Store"
                >
                  <MenuItem value="">
                    <em>None - Disable</em>
                  </MenuItem>
                  {connectedStores.map(store => (
                    <MenuItem key={store.store_id} value={store.store_id}>
                      {store.store_name} ({store.platform_name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {connectedStores.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  No connected stores. Go to Integrations to connect an e-commerce platform.
                </Typography>
              )}
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={saving || savingWeb}
              onClick={async () => {
                try {
                  setSavingWeb(true);
                  setError('');
                  setSuccess('');
                  const { data: { session } } = await supabase.auth.getSession();
                  const userId = session?.user?.id;
                  if (!userId) throw new Error('No active user session found. Please log in.');

                  const { error: upErr } = await supabase
                    .from('chatwoot_configurations')
                    .update({
                      enable_web_search: !!enableWebSearch,
                      requires_approval: !!requiresApproval,
                      enable_ecommerce_lookup: !!enableEcommerceLookup,
                      ecommerce_store_id: enableEcommerceLookup && selectedEcommerceStoreId ? selectedEcommerceStoreId : null,
                      updated_at: new Date().toISOString(),
                    })
                    .eq('rag_user_id', userId);

                  if (upErr) {
                    if (String(upErr.message || '').includes('No rows')) {
                      setError('No Chatwoot configuration found. Please provision first.');
                    } else {
                      throw new Error(upErr.message || 'Failed to save settings');
                    }
                  } else {
                    setSuccess('Settings saved.');
                  }
                } catch (e) {
                  setError(e.message || 'Failed to save settings.');
                } finally {
                  setSavingWeb(false);
                }
              }}
            >
              {savingWeb ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>

          <FormControlLabel
            control={<Checkbox checked={createAgentBot} onChange={e => setCreateAgentBot(e.target.checked)} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>Create AI agent bot</span>
                <Tooltip title="The agent bot will automatically respond to customer queries using your knowledge base. Highly recommended for automated support.">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
            }
          />
          {createAgentBot && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
              <TextField 
                label="Bot Name" 
                value={botName} 
                onChange={e => setBotName(e.target.value)} 
                fullWidth 
                placeholder={integrationName ? `${integrationName} Agent` : 'RAG Agent Bot'}
                helperText="Auto-generated from integration name, but you can customize it"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="This is the display name for your AI agent in Chatwoot. Customers won't see this, only your team members.">
                        <InfoIcon fontSize="small" color="action" />
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
              <TextField 
                label="Description" 
                value={botDescription} 
                onChange={e => setBotDescription(e.target.value)} 
                fullWidth 
                placeholder={integrationName ? `AI Agent for ${integrationName}` : 'RAG Agent Bot'}
                helperText="Internal description for your team"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Optional description to help your team understand what this bot does. Only visible in Chatwoot admin panel.">
                        <InfoIcon fontSize="small" color="action" />
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
              <FormControlLabel
                control={<Checkbox checked={attachBotToInbox} onChange={e => setAttachBotToInbox(e.target.checked)} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>Attach bot to inbox after creation</span>
                    <Tooltip title="Recommended. This connects the AI agent to your inbox so it can automatically respond to customer messages.">
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                }
              />
            </Box>
          )}

          <FormControlLabel
            control={<Checkbox checked={createInbox} onChange={e => setCreateInbox(e.target.checked)} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>Create inbox (Web Widget)</span>
                <Tooltip title="Creates a website chat widget inbox in Chatwoot. You'll receive an embed code to add to your website. Recommended for new integrations.">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
            }
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
            {createInbox && (
              <TextField 
                label="Inbox Name" 
                value={inboxName} 
                onChange={e => setInboxName(e.target.value)} 
                fullWidth 
                placeholder={integrationName || 'RAG Web Chat'}
                helperText="Auto-generated from integration name, but you can customize it"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="This is the name of your inbox in Chatwoot. It helps you identify which website or product this inbox is for. Your customers won't see this name.">
                        <InfoIcon fontSize="small" color="action" />
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            )}
            <TextField 
              label="Website URL" 
              value={websiteUrl} 
              onChange={e => setWebsiteUrl(e.target.value)} 
              fullWidth 
              placeholder="https://example.com"
              helperText={createInbox ? "The website where you'll embed the chat widget" : "Update the website URL for existing inbox (affects CSP/CORS settings)"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={createInbox ? "Enter the full URL of your website where the chat widget will be displayed (e.g., https://example.com). This enables the widget to work on your site by configuring Content Security Policy." : "Update your website URL to enable the chat widget on a different domain. This updates the Content Security Policy settings in Chatwoot."}>
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {resolvedAccountId && (
            <TextField
              label="Resolved Account ID"
              value={resolvedAccountId}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          )}
        </Box>

        {(embedSnippet || websiteToken) && (
          <Box sx={{ mt: 2 }}>
            {websiteToken && (
              <TextField
                label="Website Token"
                value={websiteToken}
                fullWidth
                sx={{ mb: 1 }}
                InputProps={{ readOnly: true }}
              />
            )}
            <TextField
              label="Embed Snippet"
              value={embedSnippet}
              onChange={e => setEmbedSnippet(e.target.value)}
              multiline
              minRows={6}
              fullWidth
              variant="outlined"
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={handleCopyEmbed} disabled={!embedSnippet}>Copy snippet</Button>
              <Button variant="contained" onClick={handleSaveEmbed} disabled={!embedSnippet}>Save snippet</Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ pb: 2, pr: 2 }}>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        <Box sx={{ position: 'relative' }}>
          <Button variant="contained" onClick={handleProvision} disabled={saving}>
            {isCreatingNew ? 'Create Integration' : 'Update Integration'}
          </Button>
          {saving && (
            <CircularProgress
              size={24}
              sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-12px', marginLeft: '-12px' }}
            />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default LocalChatwootSetupDialog;
